// Excel del listado de emergencias médicas: una fila por emergencia con los
// filtros activos de la solapa. Se arma en el navegador (xlsx-js-style) y se
// entrega por el panel de Descargas, igual que las exportaciones de luminarias.
import { IEmergencia } from 'modelos/src';
import * as XLSX from 'xlsx-js-style';
import {
  presentacionEstado,
  presentacionPrioridad,
  presentacionSituacion,
  situacionDe,
} from '../../estado-emergencia';
import {
  calcDiff,
  fechaHoraEmergencia,
  nombrePaciente,
} from '../emergencia.service';

function texto(v: unknown): string {
  if (v === undefined || v === null) return '';
  return String(v);
}

export function construirExcelEmergencias(emergencias: IEmergencia[]): Blob {
  const filas = emergencias.map((e) => {
    const det = e.ultimoEventoEmergencia?.detallesEmergencias;
    const pac = e.destinatarioAsistencia;
    const med = e.emergenciaMedica;
    return {
      Número: e.codigo ?? '',
      Situación: presentacionSituacion(situacionDe(e)).label,
      Estado: presentacionEstado(e.ultimoEventoEmergencia?.estado).label,
      'Código de apertura': e.prioridadApertura
        ? presentacionPrioridad(e.prioridadApertura).label
        : '',
      'Código de cierre': e.prioridadCierre
        ? presentacionPrioridad(e.prioridadCierre).label
        : '',
      Creación: fechaHoraEmergencia(e.fechaCreacion),
      'Primera asignación': fechaHoraEmergencia(e.fechaPrimeraAsignacion),
      'Salida de la base': fechaHoraEmergencia(e.fechaSalida),
      'Llegada a destino': fechaHoraEmergencia(e.fechaLlegadaDestino),
      'Llegada a hospital': fechaHoraEmergencia(med?.fechaLlegadaHospital),
      Finalización: fechaHoraEmergencia(e.fechaFinalizacion),
      'Tiempo hasta la asignación':
        calcDiff(e.fechaCreacion, e.fechaPrimeraAsignacion) ?? '',
      'Tiempo hasta la llegada':
        calcDiff(e.fechaCreacion, e.fechaLlegadaDestino) ?? '',
      'Duración total': calcDiff(e.fechaCreacion, e.fechaFinalizacion) ?? '',
      Reasignaciones: e.cantidadReasignaciones ?? 0,
      Paciente: nombrePaciente(pac),
      DNI: texto(pac?.dni),
      Edad: pac?.edad ?? '',
      Dirección: texto(e.direccion),
      Solicitante: texto(e.solicitante),
      Teléfono: texto(e.telefono),
      Móvil: texto(det?.movilAsignado?.identificacion),
      Chofer: texto(
        det?.chofer?.datosPersonales?.nombre || det?.chofer?.usuario,
      ),
      Médicos: (det?.medicos ?? []).map((m) => m.nombre).join(', '),
      Enfermeros: (det?.enfermeros ?? []).map((m) => m.nombre).join(', '),
      Hospital: texto(det?.hospital?.identificacion),
      'Síntomas de apertura': (med?.sintomasApertura ?? []).join(', '),
      'Síntomas de cierre': (med?.sintomasCierre ?? []).join(', '),
      Diagnóstico: texto(med?.diagnostico),
      Observaciones: texto(e.observaciones),
      Cliente: texto(e.cliente?.nombre),
    };
  });

  const ws = XLSX.utils.json_to_sheet(filas);
  ajustarColumnas(ws);
  estiloEncabezado(ws);
  const buffer = XLSX.write(
    { Sheets: { Emergencias: ws }, SheetNames: ['Emergencias'] },
    { bookType: 'xlsx', type: 'array', cellStyles: true },
  );
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

function ajustarColumnas(ws: XLSX.WorkSheet): void {
  const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][];
  if (!data.length) return;
  const columnas = data.reduce((max, row) => Math.max(max, row.length), 0);
  const anchos: { wch: number }[] = [];
  for (let c = 0; c < columnas; c++) {
    const largo = data.reduce(
      (max, row) => Math.max(max, row[c] != null ? String(row[c]).length : 0),
      0,
    );
    anchos.push({ wch: Math.min(Math.max(largo + 2, 10), 60) });
  }
  ws['!cols'] = anchos;
  ws['!autofilter'] = { ref: ws['!ref'] ?? 'A1' };
}

function estiloEncabezado(ws: XLSX.WorkSheet): void {
  const rango = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let c = rango.s.c; c <= rango.e.c; c++) {
    const celda = ws[XLSX.utils.encode_cell({ r: 0, c })];
    if (!celda) continue;
    celda.s = {
      fill: { patternType: 'solid', fgColor: { rgb: 'DCE3EA' } },
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
}
